import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, CalendarDays } from "lucide-react";
import api, { formatPrice, getStatusLabel, getStatusColor } from "@/lib/api";

const STATUSES = ["all", "pending_payment", "paid", "in_progress", "completed", "cancelled", "refunded"];
const STATUS_LABELS = { all: "Все", pending_payment: "Ожидает оплаты", paid: "Оплачено", in_progress: "В процессе", completed: "Завершено", cancelled: "Отменено", refunded: "Возврат" };
const HOURS = Array.from({ length: 17 }, (_, i) => i + 8); // 8..24
const DURATIONS = [1, 2, 3, 4, 5, 6, 8];

const EMPTY_FORM = { house_id: "", venue_type: "house", venue_id: "", date: "", start_hour: 14, duration: 2, services: [], client_name: "", client_phone: "", notes: "" };

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDate, setFilterDate] = useState("");
  const [loading, setLoading] = useState(true);

  // Manual create state
  const [createOpen, setCreateOpen] = useState(false);
  const [houses, setHouses] = useState([]);
  const [venues, setVenues] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filterStatus && filterStatus !== "all") params.set("status", filterStatus);
    if (filterDate) params.set("date", filterDate);
    api.get(`/admin/bookings?${params}`).then((r) => { setBookings(r.data.bookings); setTotal(r.data.total); }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filterStatus, filterDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/admin/houses").then(r => setHouses(r.data)).catch(() => {});
    api.get("/admin/venues").then(r => setVenues(r.data)).catch(() => {});
    api.get("/services").then(r => setAllServices(r.data)).catch(() => {});
  }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/bookings/${id}/status`, { status });
    load();
  };

  const toggleService = (id) => {
    setForm(f => ({ ...f, services: f.services.includes(id) ? f.services.filter(s => s !== id) : [...f.services, id] }));
  };

  const handleCreate = async () => {
    const isHouse = form.venue_type === "house";
    const targetId = isHouse ? form.house_id : form.venue_id;
    if (!targetId || !form.date || !form.client_name || !form.client_phone) {
      alert("Заполните: место, дата, имя, телефон");
      return;
    }
    setCreating(true);
    try {
      // For tapchan/table: bookings span the whole "session" — use a long default
      // duration (8 hours) so it covers the whole visit without time-slot conflicts
      // for ordinary walk-ins. Houses keep the granular hourly logic.
      const duration = isHouse ? form.duration : 8;
      await api.post("/admin/bookings/create", {
        house_id: isHouse ? targetId : "",
        venue_type: form.venue_type,
        venue_id: isHouse ? "" : targetId,
        date: form.date,
        start_hour: form.start_hour,
        duration,
        services: form.services,
        client_name: form.client_name,
        client_phone: form.client_phone,
        notes: form.notes,
      });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    }
    setCreating(false);
  };

  const selectedPlace = form.venue_type === "house"
    ? houses.find(h => h.id === form.house_id)
    : venues.find(v => v.id === form.venue_id);
  const placePrice = selectedPlace?.price_per_hour || 0;
  const total_preview = (() => {
    // Houses use hourly logic; tapchan/table use a flat session price (1x base price_per_hour, or 0 if free)
    const isHouse = form.venue_type === "house";
    const hc = isHouse ? placePrice * (form.duration || 0) : placePrice * 8;
    const sc = form.services.reduce((s, id) => s + (allServices.find(x => x.id === id)?.price || 0), 0);
    return hc + sc;
  })();

  return (
    <div data-testid="admin-bookings">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl">Бронирования</h1>
        <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) setForm(EMPTY_FORM); }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground rounded-none gap-2" data-testid="create-booking-btn">
              <Plus className="w-4 h-4" /> Создать бронь
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif flex items-center gap-2"><CalendarDays className="w-5 h-5 text-primary" /> Новое бронирование</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              {/* Venue type selector */}
              <div className="grid grid-cols-3 gap-2">
                <Button size="sm" variant={form.venue_type === "house" ? "default" : "outline"} onClick={() => setForm({ ...form, venue_type: "house", house_id: "", venue_id: "" })} className="rounded-none text-xs" data-testid="bk-zone-house">Банный дом</Button>
                <Button size="sm" variant={form.venue_type === "tapshan" ? "default" : "outline"} onClick={() => setForm({ ...form, venue_type: "tapshan", house_id: "", venue_id: "" })} className="rounded-none text-xs" data-testid="bk-zone-tapshan">Тапчан</Button>
                <Button size="sm" variant={form.venue_type === "table" ? "default" : "outline"} onClick={() => setForm({ ...form, venue_type: "table", house_id: "", venue_id: "" })} className="rounded-none text-xs" data-testid="bk-zone-table">Столик</Button>
              </div>

              {form.venue_type === "house" ? (
                <Select value={form.house_id} onValueChange={(v) => setForm({ ...form, house_id: v })}>
                  <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="bk-house-select"><SelectValue placeholder="Выберите домик" /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10 max-h-60">
                    {houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name_ru || h.name_kz} — {formatPrice(h.price_per_hour)}/ч</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={form.venue_id} onValueChange={(v) => setForm({ ...form, venue_id: v })}>
                  <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="bk-venue-select"><SelectValue placeholder={form.venue_type === "tapshan" ? "Выберите тапчан" : "Выберите столик"} /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10 max-h-60">
                    {venues.filter(v => v.venue_type === form.venue_type).map(v => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name_ru || v.name_kz}{v.price_per_hour ? ` — ${formatPrice(v.price_per_hour)}/ч` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {form.venue_type === "house" ? (
                <div className="grid grid-cols-3 gap-3">
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="bk-date" />
                  <Select value={String(form.start_hour)} onValueChange={(v) => setForm({ ...form, start_hour: parseInt(v) })}>
                    <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="bk-hour"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-white/10 max-h-60">
                      {HOURS.map(h => <SelectItem key={h} value={String(h)}>{h}:00</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={String(form.duration)} onValueChange={(v) => setForm({ ...form, duration: parseInt(v) })}>
                    <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="bk-duration"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-white/10 max-h-60">
                      {DURATIONS.map(d => <SelectItem key={d} value={String(d)}>{d} ч</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="bk-date" />
                  <Select value={String(form.start_hour)} onValueChange={(v) => setForm({ ...form, start_hour: parseInt(v) })}>
                    <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="bk-hour"><SelectValue placeholder="Время прихода" /></SelectTrigger>
                    <SelectContent className="bg-card border-white/10 max-h-60">
                      {HOURS.map(h => <SelectItem key={h} value={String(h)}>{h}:00</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Имя клиента" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="bk-name" />
                <Input placeholder="Телефон" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="bk-phone" />
              </div>

              <Input placeholder="Примечание" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-background border-white/10 rounded-none" />

              {/* Services picker */}
              <div className="border border-white/10 p-2 max-h-40 overflow-y-auto">
                <p className="text-xs text-muted-foreground mb-2">Доп. услуги (необязательно):</p>
                <div className="grid grid-cols-2 gap-1">
                  {allServices.filter(s => s.category !== "package").map(s => (
                    <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer py-0.5">
                      <input type="checkbox" checked={form.services.includes(s.id)} onChange={() => toggleService(s.id)} className="accent-primary" />
                      <span className="flex-1 truncate">{s.name_ru || s.name_kz}</span>
                      <span className="text-primary">{formatPrice(s.price)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {selectedPlace && (
                <div className="bg-primary/5 border border-primary/20 p-3 text-sm">
                  {form.venue_type === "house" ? (
                    <div className="flex justify-between"><span>Место ({form.duration} ч)</span><span>{formatPrice(placePrice * form.duration)}</span></div>
                  ) : placePrice > 0 ? (
                    <div className="flex justify-between"><span>Бронирование места</span><span>{formatPrice(placePrice * 8)}</span></div>
                  ) : (
                    <div className="flex justify-between text-muted-foreground"><span>Место</span><span>бесплатно</span></div>
                  )}
                  {form.services.length > 0 && <div className="flex justify-between text-xs text-muted-foreground"><span>Услуги ({form.services.length})</span><span>{formatPrice(form.services.reduce((s, id) => s + (allServices.find(x => x.id === id)?.price || 0), 0))}</span></div>}
                  <div className="flex justify-between font-serif text-primary mt-1 pt-1 border-t border-primary/20"><span>Итого</span><span>{formatPrice(total_preview)}</span></div>
                </div>
              )}

              <Button onClick={handleCreate} disabled={creating} className="w-full bg-primary text-primary-foreground rounded-none" data-testid="bk-submit">
                {creating ? "Создание..." : "Создать бронь"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-3 mb-6" data-testid="booking-filters">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] bg-card border-white/10 rounded-none" data-testid="filter-status">
            <SelectValue placeholder="Статус" />
          </SelectTrigger>
          <SelectContent className="bg-card border-white/10">
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-[180px] bg-card border-white/10 rounded-none" data-testid="filter-date" />
      </div>

      <p className="text-xs text-muted-foreground mb-3">Всего: {total}</p>

      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-16 bg-card animate-pulse" />)}</div>
      ) : bookings.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="no-bookings">Нет бронирований</div>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} className="bg-card border border-white/5 p-4" data-testid={`booking-${b.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="font-serif text-lg">{b.house_name_ru || b.house_name || "—"}</span>
                    <span className={`text-xs px-2 py-0.5 ${getStatusColor(b.status)}`}>{getStatusLabel(b.status)}</span>
                    {b.source === "admin" && <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/30 text-blue-400">Ручной</span>}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span>{b.date}</span>
                    <span>{b.start_hour}:00 — {b.end_hour}:00</span>
                    <span>{b.client_name}</span>
                    <span>{b.client_phone}</span>
                  </div>
                  {b.notes && <p className="text-xs text-muted-foreground mt-1">Заметка: {b.notes}</p>}
                  {b.utm_source && <p className="text-xs text-muted-foreground/60 mt-1">UTM: {b.utm_source}/{b.utm_medium}/{b.utm_campaign}</p>}
                </div>
                <div className="text-right">
                  <p className="text-primary font-serif text-lg">{formatPrice(b.total_amount)}</p>
                  <div className="flex flex-wrap gap-1 mt-2 justify-end">
                    {b.status === "pending_payment" && (
                      <Button size="sm" onClick={() => updateStatus(b.id, "paid")} className="bg-green-800 hover:bg-green-700 text-white rounded-none text-xs" data-testid={`mark-paid-${b.id}`}>
                        Оплачено
                      </Button>
                    )}
                    {b.status === "paid" && (
                      <Button size="sm" onClick={() => updateStatus(b.id, "in_progress")} className="bg-blue-800 hover:bg-blue-700 text-white rounded-none text-xs" data-testid={`mark-progress-${b.id}`}>
                        В процессе
                      </Button>
                    )}
                    {b.status === "in_progress" && (
                      <Button size="sm" onClick={() => updateStatus(b.id, "completed")} className="bg-gray-700 hover:bg-gray-600 text-white rounded-none text-xs" data-testid={`mark-complete-${b.id}`}>
                        Завершить
                      </Button>
                    )}
                    {["pending_payment", "paid"].includes(b.status) && (
                      <Button size="sm" variant="ghost" onClick={() => updateStatus(b.id, "cancelled")} className="text-red-400 hover:text-red-300 text-xs" data-testid={`cancel-${b.id}`}>
                        Отмена
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
